/**
 * CUSTOM: page yield info popup (snippets/product-page-yield.liquid).
 *
 * The info button ([data-page-yield-toggle]) opens the popup that follows it in the same line
 * ([data-page-yield-popup]). One set of delegated document listeners serves every card, including cards
 * inserted later by AJAX (filters, pagination, collection grouping), so nothing is bound per card.
 *
 * - Click on the button toggles the popup; only one popup is open at a time.
 * - Click outside the open popup, Escape (focus returns to the button) or moving focus out of the
 *   button and popup closes it, as does scrolling the button completely out of view. The popup is not
 *   modal, so focus is not trapped.
 * - The popup is shown with the Popover API (popover="manual") so it sits in the top layer, above the
 *   card and outside the product list's overflow: hidden. Browsers without the API get the .is-open
 *   class instead. Either way it is fixed-positioned under the button (above it when there is no room
 *   below) and kept inside the viewport.
 */
(function () {
  if (window.pageYieldPopupLoaded) {
    return;
  }

  window.pageYieldPopupLoaded = true;

  var VIEWPORT_MARGIN = 12;
  var BUTTON_GAP = 8;
  var supportsPopover = typeof HTMLElement === 'function' && HTMLElement.prototype.hasOwnProperty('popover');
  var openToggle = null;

  function popupFor(toggle) {
    return toggle.parentNode ? toggle.parentNode.querySelector('[data-page-yield-popup]') : null;
  }

  function position(toggle, popup) {
    var viewportWidth = document.documentElement.clientWidth;
    var viewportHeight = document.documentElement.clientHeight;
    var buttonRect = toggle.getBoundingClientRect();
    var width = popup.offsetWidth;
    var height = popup.offsetHeight;

    var left = Math.min(buttonRect.left - 16, viewportWidth - width - VIEWPORT_MARGIN);
    var top = buttonRect.bottom + BUTTON_GAP;

    if (top + height > viewportHeight - VIEWPORT_MARGIN && buttonRect.top - BUTTON_GAP - height >= VIEWPORT_MARGIN) {
      top = buttonRect.top - BUTTON_GAP - height;
    }

    popup.style.left = Math.max(left, VIEWPORT_MARGIN) + 'px';
    popup.style.top = Math.max(Math.min(top, viewportHeight - height - VIEWPORT_MARGIN), VIEWPORT_MARGIN) + 'px';
  }

  function close(returnFocus) {
    if (!openToggle) {
      return;
    }

    var toggle = openToggle;
    var popup = popupFor(toggle);

    openToggle = null;
    toggle.setAttribute('aria-expanded', 'false');

    if (popup) {
      if (supportsPopover) {
        if (popup.matches(':popover-open')) {
          popup.hidePopover();
        }
      } else {
        popup.classList.remove('is-open');
      }
    }

    if (returnFocus && document.body.contains(toggle)) {
      toggle.focus();
    }
  }

  function open(toggle) {
    var popup = popupFor(toggle);

    if (!popup) {
      return;
    }

    close(false);

    if (supportsPopover) {
      popup.showPopover();
    } else {
      popup.classList.add('is-open');
    }

    toggle.setAttribute('aria-expanded', 'true');
    openToggle = toggle;
    position(toggle, popup);
  }

  // A card may have been replaced by AJAX while its popup was open; forget it so the next click works.
  function openToggleIsGone() {
    if (openToggle && !document.body.contains(openToggle)) {
      openToggle = null;
    }

    return !openToggle;
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest ? event.target.closest('[data-page-yield-toggle]') : null;

    if (toggle) {
      if (toggle === openToggle) {
        close(false);
      } else {
        open(toggle);
      }

      return;
    }

    if (!openToggleIsGone() && !event.target.closest('[data-page-yield-popup]')) {
      close(false);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && !openToggleIsGone()) {
      close(true);
    }
  });

  document.addEventListener('focusin', function (event) {
    if (openToggleIsGone()) {
      return;
    }

    var popup = popupFor(openToggle);

    if (event.target !== openToggle && !(popup && popup.contains(event.target))) {
      close(false);
    }
  });

  // Once the button has scrolled completely out of view the popup has nothing to point at, so it closes
  // (without moving focus to the hidden button) instead of staying pinned to the viewport edge.
  function toggleIsOutOfView(toggle) {
    var rect = toggle.getBoundingClientRect();

    return (
      rect.bottom <= 0 ||
      rect.right <= 0 ||
      rect.top >= document.documentElement.clientHeight ||
      rect.left >= document.documentElement.clientWidth
    );
  }

  function reposition() {
    if (openToggleIsGone()) {
      return;
    }

    if (toggleIsOutOfView(openToggle)) {
      close(false);
      return;
    }

    var popup = popupFor(openToggle);

    if (popup) {
      position(openToggle, popup);
    }
  }

  window.addEventListener('resize', reposition);
  window.addEventListener('scroll', reposition, { capture: true, passive: true });
})();
